import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { QaiFieldComponent, QaiInputComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective } from '@qai/ui';
import { PreviewSimulationDetailsModalComponent } from 'src/app/pages/self-serve/simulation-builder/components/modals/preview-simulation-details/preview-simulation-modal.component';
import { debounceTime, distinctUntilChanged, skip, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import {
  ISimulationDetail,
  ISimulationDetailsForm,
} from 'src/app/interfaces/self-serve/isimulation-details-information';
import { ISimulationDetails } from 'src/app/interfaces/self-serve/responses/isimulation-details';
import { ISimulationDetailInformationTab } from 'src/app/interfaces/self-serve/isimulation-details-information-tab';

@Component({
  selector: 'simulation-details-information',
  standalone: true,
  templateUrl: './simulation-details-information.component.html',
  styleUrls: ['./simulation-details-information.component.scss'],
  imports: [
    QaiFieldComponent,
    QaiInputComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    DetailsSectionComponent,
    PreviewSimulationDetailsModalComponent,
    TextareaAutosizeDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiTrimLeadingDirective,
    QaiPasteCleanDirective
],
})
export class SimulationDetailsInformationComponent {
  form!: FormGroup;
  simulationTabs!: FormGroup;
  openPreviewModal = false;
  previewTabs: { label: string; content: string }[] = [];

  previewModalButtonState: boolean = true;
  simulationDetailsSectionDescription: string =
    'This is the content that will be provided to the user before they begin the Simulation. Any sections left blank will not be shown to the learner. All sections support markdown formatting.';

  constructor(
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const uuid = this.route.snapshot.paramMap.get('uuid')!;

    this.authorReactiveFormService
      .getForm(uuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.form = form.get(
          SimulationConstants.SIMULATION_DETAILS_INFO_PATH
        ) as FormGroup;

        this.simulationTabs = this.form.get(
          'simulationDetailtabs'
        ) as FormGroup;

        const tabControls = Object.values(this.simulationTabs.controls);

        this.previewModalButtonState = !tabControls.some((control) => {
          const value = control.value;
          return value?.tabText && value.tabText.trim() !== '';
        });

        this.setupAutoSave(uuid);
      });
  }

  private setupAutoSave(uuid: string): void {
    const additionalSectionTitleControl = this.form.get(
      'additionalSectionTitle'
    );
    const additionalSectionInfoControl = this.form.get('additionalSectionInfo');

    let previousFormValue = this.form.getRawValue();

    this.form.valueChanges
      .pipe(
        skip(1), // ignore initial form init change
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newFormValue) => {
        const oldFormValue = previousFormValue;
        previousFormValue = newFormValue;

        const updatedFormValue = this.form.getRawValue();

        const hasRelevantChanges = this.hasRelevantChanges(
          oldFormValue,
          newFormValue
        );

        if (!hasRelevantChanges) {
          return;
        }

        this.authorReactiveFormService.patchForm(
          uuid,
          SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
          updatedFormValue
        );

        this.previewModalButtonState =
          !updatedFormValue.simulationDetailtabs.some(
            (tab: { tabText: string }) =>
              tab.tabText && tab.tabText.trim() !== ''
          );

        if (updatedFormValue) {
          const { callDetails, isAlreadyCreated } =
            this.prepareCallDetailsPayload(updatedFormValue);

          if (!isAlreadyCreated) {
            this.createSimulationDetail(uuid, callDetails);
          } else {
            this.updateSimulationDetail(callDetails);
          }
        }
      });
  }

  private hasRelevantChanges(oldValue: any, newValue: any): boolean {
    const oldTabs = oldValue?.simulationDetailtabs || [];
    const newTabs = newValue?.simulationDetailtabs || [];

    for (let i = 0; i < Math.max(oldTabs.length, newTabs.length); i++) {
      const oldTabText = (oldTabs[i]?.tabText || '').trim();
      const newTabText = (newTabs[i]?.tabText || '').trim();

      if (oldTabText !== newTabText) {
        return true;
      }
    }

    const oldAdditionalTitle = (oldValue?.additionalSectionTitle || '').trim();
    const newAdditionalTitle = (newValue?.additionalSectionTitle || '').trim();

    const oldAdditionalInfo = (oldValue?.additionalSectionInfo || '').trim();
    const newAdditionalInfo = (newValue?.additionalSectionInfo || '').trim();

    return (
      oldAdditionalTitle !== newAdditionalTitle ||
      oldAdditionalInfo !== newAdditionalInfo
    );
  }

  get simulationDetailtabs(): FormArray {
    return this.form.get(
      SimulationConstants.SIMULATION_DETAILS_TABS_SECTION
    ) as FormArray;
  }

  createSimulationDetail(uuid: string, callDetails: ISimulationDetail[]) {
    this.authorService.createSimulationDetails(uuid, callDetails).subscribe({
      next: (res: ISimulationDetails) => {
        console.log(res);
        const normalized =
          this.authorReactiveFormService.normalizeCallDetailsToForm(
            res.callDetails
          );
        this.authorReactiveFormService.patchForm(
          uuid,
          SimulationConstants.SIMULATION_DETAILS_INFO_PATH,
          normalized
        );
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  updateSimulationDetail(callDetails: ISimulationDetail[]) {
    this.authorService.updateSimulationDetails(callDetails).subscribe({
      next: (res: ISimulationDetails) => {
        console.log(res);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  prepareCallDetailsPayload(value: ISimulationDetailsForm): {
    callDetails: ISimulationDetail[];
    isAlreadyCreated: boolean;
  } {
    const {
      simulationDetailtabs,
      additionalSectionTitle,
      additionalSectionInfo,
      additionalSectionUuid,
    } = value;

    const detailTabs = simulationDetailtabs.map((tab, idx) => ({
      tabSequence: idx + 1,
      tabTitle: tab.tabTitle,
      tabText: tab.tabText,
      uuid: tab.uuid || null,
    }));

    const additionalSection = {
      tabSequence: detailTabs.length + 1,
      tabTitle: additionalSectionTitle,
      tabText: additionalSectionInfo,
      uuid: additionalSectionUuid || null,
    };

    const callDetails = [...detailTabs, additionalSection];

    const isAlreadyCreated = callDetails.every((d) => !!d.uuid);

    return { callDetails, isAlreadyCreated };
  }

  previewModal() {
    const {
      simulationDetailtabs,
      additionalSectionTitle,
      additionalSectionInfo,
    } = this.form.value;
    const detailTabs = simulationDetailtabs.map(
      (tab: ISimulationDetailInformationTab) => ({
        label: tab.tabTitle,
        content: tab.tabText,
      })
    );

    if (additionalSectionTitle && additionalSectionInfo) {
      detailTabs.push({
        label: additionalSectionTitle,
        content: additionalSectionInfo,
      });
    }

    this.previewTabs = detailTabs;
    this.openPreviewModal = true;
  }

  handleModalClose() {
    this.openPreviewModal = false;
  }
}
